import { Combobox, TextField, Typography } from "@hellocarbo/lasagne";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  Suspense,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Await } from "react-router";

import useSignupSteps from "@/hooks/useSignupSteps";

import Template from "./Template";

export type IndustryGroups = {
  name: string;
  items: { value: string; label: string }[];
}[];

const CompanyStep = ({
  companyName,
  setCompanyName,
  industry,
  setIndustry,
  industryResponse,
}: {
  companyName: string;
  setCompanyName: Dispatch<SetStateAction<string>>;
  industry: string;
  setIndustry: Dispatch<SetStateAction<string>>;
  industryResponse: Promise<{
    data: IndustryGroups;
  }>;
}) => {
  const { t } = useTranslation(["common", "signUp"]);
  const { goToNextStep } = useSignupSteps();

  const [hasCompanyNameError, setHasCompanyNameError] = useState(false);
  const [hasIndustryError, setHasIndustryError] = useState(false);

  const handleContinue = () => {
    if (!companyName) {
      setHasCompanyNameError(true);
    }
    if (!industry) {
      setHasIndustryError(true);
    }

    if (companyName && industry) {
      goToNextStep();
    }
  };

  return (
    <Template
      title={t("signUp:company.title")}
      buttonText={t("continue")}
      onClick={handleContinue}
    >
      <div className="space-y-md">
        <div>
          <TextField
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setCompanyName(e.target.value)
            }
            type="text"
            name="companyName"
            value={companyName}
            label={t("signUp:company.name.label")}
            error={
              hasCompanyNameError ? t("signUp:company.name.error") : undefined
            }
            fullWidth
          />
        </div>
        <Suspense
          fallback={
            <Combobox
              loading={true}
              groups={[]}
              label={t("signUp:company.industry.label")}
            />
          }
        >
          <Await resolve={industryResponse}>
            {({ data: industryGroups }) => {
              // rename "name" to "groupName" for Combobox
              industryGroups = industryGroups.map((group) => ({
                ...group,
                groupName: group.name,
              }));

              //check if industry exists in the industry groups
              const industryExists = industryGroups.some(
                (group) => group.name === industry
              );

              return (
                <Combobox
                  label={t("signUp:company.industry.label")}
                  placeholder={t("signUp:company.industry.placeholder")}
                  groups={industryGroups}
                  onChange={(value) => setIndustry(value)}
                  defaultValue={industry}
                />
              );
            }}
          </Await>
        </Suspense>
        {/* TODO: fix once https://linear.app/hellocarbo/issue/LAS-142/ajouter-un-state-error-au-combobox is done */}
        {hasIndustryError ? (
          <Typography className="text-negative">
            {t("signUp:company.industry.error")}
          </Typography>
        ) : undefined}
      </div>
    </Template>
  );
};

export default CompanyStep;
